import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie, Legend, LineChart, Line, LabelList,
} from 'recharts';

// Theme-aligned palette (primary blue first, then a calm categorical set).
const PALETTE = ['#3B6FB6', '#D9482B', '#3E9C7B', '#E0A53B', '#8A63B8', '#5BA3C9', '#C9637E', '#6B7280'];
const PRIMARY = '#3B6FB6';

const pick = (ml, lang) => {
  if (!ml) return '';
  if (typeof ml === 'string') return ml;
  return ml[lang] || ml.en || ml.fr || ml.pt || '';
};

const truncate = (s, n) => {
  s = String(s ?? '');
  return s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const row = p.payload || {};
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2 text-xs shadow-md">
      <p className="font-medium">{row.label ?? label}</p>
      <p className="text-muted-foreground tabular-nums">
        {p.name}: <span className="font-semibold text-foreground">{p.value}</span>
        {row.count != null ? ` · ${row.count} ${row.count === 1 ? 'student' : 'students'}` : ''}
      </p>
    </div>
  );
};

// Datasets whose labels are person names — always best shown as horizontal bars.
const PERSON_NAME_DATASETS = new Set(['top_performers', 'bottom_performers']);

/**
 * Renders one AI-chosen chart from a resolved spec:
 *   { type: 'bar'|'hbar'|'pie'|'donut'|'line', title, caption, data:[{label,value,count?}], max }
 */
export const DynamicChart = ({ chart, lang = 'en', animate = true }) => {
  if (!chart || !Array.isArray(chart.data) || chart.data.length === 0) return null;
  const data = chart.data;
  const title = pick(chart.title, lang);
  const caption = pick(chart.caption, lang);
  const domainMax = chart.max || undefined;

  // Long category labels (e.g. full student names) overlap on a vertical axis.
  // Auto-promote such bar charts to horizontal so every label is readable.
  const longest = data.reduce((m, d) => Math.max(m, String(d.label || '').length), 0);
  let renderType = chart.type;
  if (renderType === 'bar' && (PERSON_NAME_DATASETS.has(chart.dataset) || longest > 18)) {
    renderType = 'hbar';
  }

  let body = null;
  if (renderType === 'pie' || renderType === 'donut') {
    body = (
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius={renderType === 'donut' ? 48 : 0}
          outerRadius={78}
          paddingAngle={2}
          isAnimationActive={animate}
          label={(e) => `${e.value}`}
        >
          {data.map((entry, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    );
  } else if (renderType === 'line') {
    body = (
      <LineChart data={data} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.6} vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} tickFormatter={(v) => truncate(v, 16)} stroke="hsl(var(--muted-foreground))" />
        <YAxis domain={[0, domainMax || 'auto']} allowDecimals tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
        <Tooltip content={<ChartTooltip />} />
        <Line type="monotone" dataKey="value" name="Average" stroke={PRIMARY} strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} isAnimationActive={animate} />
      </LineChart>
    );
  } else if (renderType === 'hbar') {
    body = (
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 30, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} horizontal={false} />
        <XAxis type="number" domain={[0, domainMax || 'auto']} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
        <YAxis
          type="category"
          dataKey="label"
          width={150}
          interval={0}
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => truncate(v, 20)}
          stroke="hsl(var(--muted-foreground))"
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(59,111,182,0.08)' }} />
        <Bar dataKey="value" name="Score" fill={PRIMARY} radius={[0, 4, 4, 0]} isAnimationActive={animate}>
          <LabelList dataKey="value" position="right" style={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
        </Bar>
      </BarChart>
    );
  } else {
    // default: vertical bar (short labels). Rotate + give room so all show.
    const angled = longest > 6 || data.length > 4;
    body = (
      <BarChart data={data} margin={{ top: 14, right: 12, left: -10, bottom: angled ? 8 : 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10 }}
          interval={0}
          tickFormatter={(v) => truncate(v, 14)}
          angle={angled ? -30 : 0}
          textAnchor={angled ? 'end' : 'middle'}
          height={angled ? 64 : 28}
          stroke="hsl(var(--muted-foreground))"
        />
        <YAxis domain={[0, domainMax || 'auto']} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(59,111,182,0.08)' }} />
        <Bar dataKey="value" name="Score" fill={PRIMARY} radius={[4, 4, 0, 0]} isAnimationActive={animate}>
          <LabelList dataKey="value" position="top" style={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
        </Bar>
      </BarChart>
    );
  }

  const height = (renderType === 'hbar') ? Math.max(180, data.length * 40 + 40) : 248;

  return (
    <div className="border border-border rounded-lg p-4 bg-background" data-testid={`ai-chart-${chart.dataset || renderType}`}>
      {title ? <h4 className="text-sm font-semibold mb-1">{title}</h4> : null}
      {caption ? <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{caption}</p> : null}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          {body}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DynamicChart;
